import { useChatStore } from "../../../state/chat";
import { askAboutAndReveal } from "../../../state/chatActions";
import type { MerchantSummary, Portfolio } from "../../../data/types";
import { fmtEur, fmtPct } from "./format";
import styles from "./WorstOffenders.module.css";

interface WorstOffendersProps {
  portfolio: Portfolio;
  merchantLimit?: number;
  mccLimit?: number;
}

export function WorstOffenders({
  portfolio,
  merchantLimit = 5,
  mccLimit = 3,
}: WorstOffendersProps) {
  const pushDrillHint = useChatStore((s) => s.pushDrillHint);

  // Rank merchants by current fraudRatioPct, ignore fully terminated (already off the book).
  const merchants = [...portfolio.merchants]
    .filter((m) => m.status !== "Terminated")
    .sort((a, b) => b.fraudRatioPct - a.fraudRatioPct)
    .slice(0, merchantLimit);

  // Roll up by MCC.
  const byMcc = new Map<
    string,
    { mcc: string; desc: string; vol: number; tc: number; purchases: number; count: number }
  >();
  for (const m of portfolio.merchants) {
    if (m.status === "Terminated") continue;
    const row = byMcc.get(m.mcc) ?? {
      mcc: m.mcc,
      desc: m.mccDesc,
      vol: 0,
      tc: 0,
      purchases: 0,
      count: 0,
    };
    row.vol += m.totalVolume;
    row.tc += m.totalTc40 + m.totalTc15;
    row.purchases += m.totalPurchases;
    row.count += 1;
    byMcc.set(m.mcc, row);
  }
  const mccRows = [...byMcc.values()]
    .map((r) => ({
      ...r,
      ratio: r.purchases > 0 ? (r.tc / r.purchases) * 100 : 0,
    }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, mccLimit);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>worst offenders</span>
        <span className={styles.muted}>ranked by vamp — click to drill in</span>
      </header>

      <div className={styles.split}>
        <section className={styles.column}>
          <h4 className={styles.subtitle}>mccs</h4>
          <ul className={styles.list}>
            {mccRows.map((r) => (
              <li key={r.mcc}>
                <button
                  type="button"
                  className={styles.row}
                  onClick={() => {
                    pushDrillHint(
                      `◈ filtered the cohort to mcc ${r.mcc} (${r.desc}) — ${r.count} merchants · ${fmtPct(r.ratio, 2)} pooled vamp.`
                    );
                    askAboutAndReveal(
                      `break down mcc ${r.mcc} (${r.desc}). which merchants are in this segment, which are closest to breach, and what's driving the pooled ${fmtPct(r.ratio, 2)} vamp?`,
                      "intelligence",
                      { kind: "cohort", filter: r.mcc }
                    );
                  }}
                  title={`drill into mcc ${r.mcc}`}
                >
                  <span className={styles.rowLeft}>
                    <span className={styles.code}>{r.mcc}</span>
                    <span className={styles.desc}>{r.desc}</span>
                  </span>
                  <span className={styles.rowRight}>
                    <span className={styles.rate}>{fmtPct(r.ratio, 2)}</span>
                    <span className={styles.rateMuted}>{r.count} merchants</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.column}>
          <h4 className={styles.subtitle}>merchants</h4>
          <ul className={styles.list}>
            {merchants.map((m) => (
              <li key={m.id}>
                <MerchantRow
                  merchant={m}
                  onOpen={() => {
                    pushDrillHint(
                      `◈ pulled up ${m.name} — ${m.mccDesc} · ${fmtPct(m.fraudRatioPct)} vamp on ${fmtEur(m.totalVolume)}.`
                    );
                    askAboutAndReveal(
                      `walk me through ${m.name} (${m.id}) — what's driving their ${fmtPct(m.fraudRatioPct)} vamp, what intervention history do they have, and who else is on their cluster?`,
                      "intelligence",
                      { kind: "merchant", merchantId: m.id }
                    );
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function MerchantRow({
  merchant,
  onOpen,
}: {
  merchant: MerchantSummary;
  onOpen: () => void;
}) {
  return (
    <button type="button" className={styles.row} onClick={onOpen}>
      <span className={styles.rowLeft}>
        <span className={styles.code}>{merchant.id}</span>
        <span className={styles.desc}>{merchant.name}</span>
      </span>
      <span className={styles.rowRight}>
        <span className={styles.rate}>{fmtPct(merchant.fraudRatioPct)}</span>
        <span className={styles.rateMuted}>{fmtEur(merchant.totalVolume)}</span>
      </span>
    </button>
  );
}
